
import React, { useEffect, useState } from 'react';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import BasicInformation from './line-items/BasicInformation';
import UnitCostFields from './line-items/UnitCostFields';
import ConversionFields from './line-items/ConversionFields';
import ProductivityFields from './line-items/ProductivityFields';
import TagsInput from './line-items/TagsInput';
import PricingTiers from './line-items/PricingTiers';
import LineItemFormActions from './line-items/LineItemFormActions';
import ImageUploader from './line-items/ImageUploader';
import CategoriesAndTradesFields from './line-items/CategoriesAndTradesFields';
import CustomMarkupTiers from './line-items/markup-tiers/CustomMarkupTiers';
import PricingSummaryCard from './line-items/PricingSummaryCard';
import CustomMarkupOverride from './line-items/CustomMarkupOverride';
import { useLineItemForm } from './line-items/hooks/useLineItemForm';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBrowseCategories } from '@/components/estimates/line-items/hooks/useBrowseCategories';
import { ExternalLink, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface InternalLineItemFormProps {
  lineItemId?: string;
}

interface PlatformSupplier {
  id: string;
  name: string;
  website: string | null;
}

interface CompanySupplier {
  id: string;
  name: string;
}

interface PriceSource {
  id: string | null;
  sort_order: 1 | 2 | 3;
  platform_supplier_id: string | null;
  supplier_id: string | null;
  source_name: string;
  url: string;
  last_seen_price: string;
  last_checked_at: string | null;
  is_dirty: boolean;
}

const STALE_DAYS = 90;
const isStale = (iso: string | null): boolean => {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > STALE_DAYS * 24 * 60 * 60 * 1000;
};


interface PickerCat {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
  company_id?: string | null;
  is_global?: boolean;
}

const BrowseCategoryPickerNode = ({
  category,
  allCategories,
  selectedId,
  onSelect,
  depth,
}: {
  category: PickerCat;
  allCategories: PickerCat[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  depth: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const children = allCategories
    .filter((c) => c.parent_id === category.id)
    .sort(
      (a, b) =>
        a.display_order - b.display_order || a.name.localeCompare(b.name)
    );
  const hasChildren = children.length > 0;
  const isSelected = selectedId === category.id;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer hover:bg-muted/60 ${
          isSelected ? 'bg-primary/10 text-primary font-medium' : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelect(isSelected ? null : category.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="w-4 text-xs text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((p) => !p);
            }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-4 inline-block" />
        )}
        <span className="text-sm">{category.name}</span>
        {!category.is_global && category.company_id && (
          <span className="ml-1 text-[10px] font-medium px-1 py-0.5 rounded bg-primary/10 text-primary">
            Custom
          </span>
        )}
      </div>
      {hasChildren && expanded && (
        <div>
          {children.map((child) => (
            <BrowseCategoryPickerNode
              key={child.id}
              category={child}
              allCategories={allCategories}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const InternalLineItemForm: React.FC<InternalLineItemFormProps> = ({ lineItemId }) => {
  const {
    form,
    isLoading,
    isEditMode,
    pricingTiers,
    setPricingTiers,
    enableConversion,
    setEnableConversion,
    enableProductivity,
    setEnableProductivity,
    imageFiles,
    setImageFiles,
    imagePreviewUrls,
    setImagePreviewUrls,
    selectedCategory,
    setSelectedCategory,
    selectedTrade,
    setSelectedTrade,
    selectedBrowseCategory,
    setSelectedBrowseCategory,
    onSubmit,
    handleCancel,
    // Markup calculation props
    companyId,
    baseCost,
    effectiveMarkup,
    effectiveMargin,
    finalPrice,
    overrideMarkup,
    validCustomTiers,
    useCustomTiers,
    markupResult,
    // Markup handlers
    handleOverrideToggle,
    handleCustomMarkupChange,
    handleCustomMarginChange,
    handlePricingRefreshed
  } = useLineItemForm({ lineItemId });

  const { flat: browseCategories } = useBrowseCategories();
  const [sources, setSources] = useState<PriceSource[]>([]);
  const [savingSources, setSavingSources] = useState(false);
  const [openSlot, setOpenSlot] = useState<number | null>(null);


  const queryClient = useQueryClient();

  const platformSuppliersQuery = useQuery({
    queryKey: ['ili-platform-suppliers'],
    queryFn: async (): Promise<PlatformSupplier[]> => {
      const { data, error } = await (supabase as any)
        .from('platform_suppliers')
        .select('id, name, website')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return (data ?? []) as PlatformSupplier[];
    },
  });

  const companySuppliersQuery = useQuery({
    queryKey: ['ili-company-suppliers', companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<CompanySupplier[]> => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('company_id', companyId!)
        .order('name');
      if (error) throw error;
      return (data ?? []) as CompanySupplier[];
    },
  });

  const sourcesQuery = useQuery({
    queryKey: ['ili-sources', lineItemId],
    enabled: !!lineItemId,
    queryFn: async (): Promise<PriceSource[]> => {
      const { data, error } = await (supabase as any)
        .from('internal_line_item_price_sources')
        .select('*')
        .eq('internal_line_item_id', lineItemId!)
        .order('sort_order');
      if (error) throw error;
      return ((data ?? []) as any[]).map((r) => ({
        id: r.id,
        sort_order: r.sort_order,
        platform_supplier_id: r.platform_supplier_id ?? null,
        supplier_id: r.supplier_id ?? null,
        source_name: r.source_name ?? '',
        url: r.url ?? '',
        last_seen_price: r.last_seen_price != null ? String(r.last_seen_price) : '',
        last_checked_at: r.last_checked_at ?? null,
        is_dirty: false,
      }));
    },
  });

  useEffect(() => {
    if (sourcesQuery.data) {
      setSources(sourcesQuery.data);
    }
  }, [sourcesQuery.data]);

  const buildCategoryPath = (id: string | null): string => {
    if (!id || !browseCategories.length) return '';
    const parts: string[] = [];
    let current: PickerCat | undefined = browseCategories.find((c) => c.id === id);
    while (current) {
      parts.unshift(current.name);
      current = current.parent_id
        ? browseCategories.find((c) => c.id === current!.parent_id)
        : undefined;
    }
    return parts.join(' > ');
  };

  const updateSource = (sortOrder: 1 | 2 | 3, patch: Partial<PriceSource>) => {
    setSources((prev) => {
      const existing = prev.find((s) => s.sort_order === sortOrder);
      if (existing) {
        return prev.map((s) =>
          s.sort_order === sortOrder ? { ...s, ...patch, is_dirty: true } : s
        );
      } else {
        const newSource: PriceSource = {
          id: null,
          sort_order: sortOrder,
          platform_supplier_id: null,
          supplier_id: null,
          source_name: '',
          url: '',
          last_seen_price: '',
          last_checked_at: null,
          is_dirty: true,
          ...patch,
        };
        return [...prev, newSource];
      }
    });
  };

  const markChecked = async (source: PriceSource) => {
    const now = new Date().toISOString();
    if (source.id) {
      await (supabase as any)
        .from('internal_line_item_price_sources')
        .update({ last_checked_at: now })
        .eq('id', source.id);
      queryClient.invalidateQueries({ queryKey: ['ili-sources', lineItemId] });
    }
    setSources((prev) =>
      prev.map((s) =>
        s.sort_order === source.sort_order ? { ...s, last_checked_at: now } : s
      )
    );
  };

  const saveSources = async () => {
    if (!lineItemId) return;
    setSavingSources(true);
    let savedCount = 0;
    let skippedCount = 0;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profileData } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user?.id ?? '')
        .single();
      const companyIdForSave = profileData?.company_id;

      if (!companyIdForSave) {
        toast.error('Could not determine your company. Please refresh and try again.');
        return;
      }

      for (const source of sources) {
        const hasContent =
          source.platform_supplier_id ||
          source.supplier_id ||
          source.source_name.trim() ||
          source.url.trim() ||
          source.last_seen_price.trim();

        if (!hasContent) {
          if (source.id) {
            const { error } = await (supabase as any)
              .from('internal_line_item_price_sources')
              .delete()
              .eq('id', source.id);
            if (error) {
              console.error('Delete error:', error);
              toast.error(`Failed to remove Source ${source.sort_order}: ${error.message}`);
            }
          }
          continue;
        }

        if (!source.platform_supplier_id && !source.supplier_id && !source.source_name.trim()) {
          toast.error(`Source ${source.sort_order}: select a supplier or enter a source name`);
          skippedCount++;
          continue;
        }

        if (!source.last_seen_price.trim()) {
          toast.error(`Source ${source.sort_order}: last seen price is required`);
          skippedCount++;
          continue;
        }

        const urlVal = source.url.trim();
        if (urlVal && !/^https?:\/\//i.test(urlVal)) {
          toast.error(`Source ${source.sort_order}: URL must start with http:// or https://`);
          skippedCount++;
          continue;
        }

        const payload = {
          internal_line_item_id: lineItemId,
          company_id: companyIdForSave,
          sort_order: source.sort_order,
          platform_supplier_id: source.platform_supplier_id || null,
          supplier_id: source.supplier_id || null,
          source_name: source.source_name.trim() || null,
          url: urlVal || null,
          last_seen_price: source.last_seen_price.trim()
            ? Number(source.last_seen_price)
            : null,
        };

        if (source.id) {
          const { error } = await (supabase as any)
            .from('internal_line_item_price_sources')
            .update(payload)
            .eq('id', source.id);
          if (error) {
            console.error('Update error:', error);
            toast.error(`Source ${source.sort_order}: ${error.message}`);
            skippedCount++;
            continue;
          }
        } else {
          const { data: inserted, error } = await (supabase as any)
            .from('internal_line_item_price_sources')
            .insert(payload)
            .select()
            .single();
          if (error) {
            console.error('Insert error:', error);
            toast.error(`Source ${source.sort_order}: ${error.message}`);
            skippedCount++;
            continue;
          }
          if (inserted) {
            setSources(prev => prev.map(s =>
              s.sort_order === source.sort_order
                ? { ...s, id: (inserted as any).id, is_dirty: false }
                : s
            ));
            savedCount++;
            continue;
          }
        }
        savedCount++;
      }

      if (savedCount > 0) {
        toast.success(`${savedCount} source${savedCount > 1 ? 's' : ''} saved`);
      } else if (skippedCount === 0) {
        toast.info('No changes to save');
      }

      await queryClient.refetchQueries({ queryKey: ['ili-sources', lineItemId] });
      setSources(prev => prev.map(s => ({ ...s, is_dirty: false })));
    } catch (err) {
      console.error('saveSources error:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save sources');
    } finally {
      setSavingSources(false);
    }
  };


  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>{isEditMode ? 'Edit' : 'Add'} Internal Line Item</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form id="lineItemForm" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <BasicInformation form={form} />
            
            <ImageUploader 
              imagePreviewUrls={imagePreviewUrls}
              setImagePreviewUrls={setImagePreviewUrls}
              imageFiles={imageFiles}
              setImageFiles={setImageFiles}
              form={form}
            />

            {/* Only Base Cost Fields - no redundant conversion factor or custom markup */}
            <UnitCostFields form={form} />

            {/* Pricing Summary Card - Positioned directly after base cost for real-time visibility */}
            <PricingSummaryCard
              baseCost={baseCost}
              effectiveMarkup={effectiveMarkup}
              effectiveMargin={effectiveMargin}
              finalPrice={finalPrice}
              overrideMarkup={overrideMarkup}
              onOverrideToggle={handleOverrideToggle}
              markupResult={markupResult}
              lineItemId={lineItemId}
              categoryId={selectedCategory?.id}
              companyId={companyId}
              customTiers={validCustomTiers}
              useCustomTiers={useCustomTiers}
              onPricingRefreshed={handlePricingRefreshed}
            />

            {/* Custom Markup Override - Shows when override is enabled (markup only, no margin) */}
            {overrideMarkup && (
              <CustomMarkupOverride
                form={form}
                onCustomMarkupChange={handleCustomMarkupChange}
                onCustomMarginChange={handleCustomMarginChange}
              />
            )}

            {/* Conversion Fields - Only shown when checkbox is enabled */}
            <ConversionFields 
              form={form} 
              enableConversion={enableConversion}
              setEnableConversion={setEnableConversion}
            />

            {/* Productivity Fields - Only shown when checkbox is enabled */}
            <ProductivityFields 
              form={form} 
              enableProductivity={enableProductivity}
              setEnableProductivity={setEnableProductivity}
            />

            <Separator />

            <CategoriesAndTradesFields
              form={form}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedTrade={selectedTrade}
              setSelectedTrade={setSelectedTrade}
            />

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Browse Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Assign a browse category so this item appears when browsing by
                  category in the catalog and line items page.
                </p>

                {selectedBrowseCategory && (
                  <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
                    <span className="text-sm font-medium">
                      {buildCategoryPath(selectedBrowseCategory) || 'Selected'}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedBrowseCategory(null)}
                    >
                      Remove
                    </Button>
                  </div>
                )}

                <div className="max-h-72 overflow-y-auto rounded-md border p-2">
                  <div
                    className={`py-1.5 px-2 rounded cursor-pointer text-sm hover:bg-muted/60 ${
                      !selectedBrowseCategory ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground'
                    }`}
                    onClick={() => setSelectedBrowseCategory(null)}
                  >
                    None
                  </div>
                  {browseCategories
                    .filter((c) => !c.parent_id)
                    .sort(
                      (a, b) =>
                        a.display_order - b.display_order ||
                        a.name.localeCompare(b.name)
                    )
                    .map((root) => (
                      <BrowseCategoryPickerNode
                        key={root.id}
                        category={root}
                        allCategories={browseCategories}
                        selectedId={selectedBrowseCategory}
                        onSelect={setSelectedBrowseCategory}
                        depth={0}
                      />
                    ))}
                </div>
              </CardContent>
            </Card>


            <Separator />

            <CustomMarkupTiers 
              form={form}
              baseCost={baseCost}
            />

            <TagsInput form={form} />

            <Separator />

            <PricingTiers 
              pricingTiers={pricingTiers}
              setPricingTiers={setPricingTiers}
              baseCost={baseCost}
              markupPercentage={effectiveMarkup}
            />

            {/* Price Sources */}
            <Separator />
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Price Sources</CardTitle>
                {lineItemId && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={saveSources}
                    disabled={savingSources || !sources.some((s) => s.is_dirty)}
                    className="h-7 text-xs"
                  >
                    {savingSources ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Save Sources
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Add up to 3 sources where this item's price can be verified. Sources are visible to estimators (name only).
                </p>

                {lineItemId ? (
                  <div className="space-y-2">
                    {([1, 2, 3] as const).map((slot) => {
                      const source = sources.find((s) => s.sort_order === slot);
                      const platformSuppliers = platformSuppliersQuery.data ?? [];
                      const companySuppliers = companySuppliersQuery.data ?? [];
                      const stale = isStale(source?.last_checked_at ?? null);
                      const isOpen = openSlot === slot;

                      const supplierName = source?.platform_supplier_id
                        ? platformSuppliers.find(ps => ps.id === source.platform_supplier_id)?.name
                        : source?.supplier_id
                        ? companySuppliers.find(cs => cs.id === source.supplier_id)?.name
                        : null;
                      const priceDisplay = source?.last_seen_price ? ` — R${Number(source.last_seen_price).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '';
                      const summary = supplierName ?? source?.source_name ?? (source?.url ? source.url.slice(0, 30) + (source.url.length > 30 ? '…' : '') : null);
                      const summaryWithPrice = summary ? `${summary}${priceDisplay}` : null;

                      return (
                        <div key={slot} className="rounded-md border">
                          <button
                            type="button"
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/40"
                            onClick={() => setOpenSlot(isOpen ? null : slot)}
                          >
                            <div className="flex items-center gap-2">
                              {isOpen
                                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              }
                              <span className="text-sm font-medium">
                                Source {slot}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground truncate max-w-[60%] text-right">
                              {summaryWithPrice ?? 'Empty'}
                            </span>
                          </button>

                          {isOpen && (
                            <div className="px-3 pb-3 space-y-3 border-t">
                              {source?.last_checked_at && (
                                <div className="flex justify-end pt-2">
                                  <span className="text-xs text-muted-foreground">
                                    Last checked: {new Date(source.last_checked_at).toLocaleDateString()}
                                  </span>
                                </div>
                              )}

                              <div className="space-y-1">
                                <Label className="text-xs">Supplier Directory</Label>
                                <p className="text-[11px] text-muted-foreground">Managed by Doyence — available to all companies</p>
                                <Select
                                  value={source?.platform_supplier_id ?? '__none__'}
                                  onValueChange={(v) =>
                                    updateSource(slot, {
                                      platform_supplier_id: v === '__none__' ? null : v,
                                      supplier_id: null,
                                    })
                                  }
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— None —</SelectItem>
                                    {platformSuppliers.map((ps) => (
                                      <SelectItem key={ps.id} value={ps.id}>{ps.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {!source?.platform_supplier_id && (
                                <div className="space-y-1">
                                  <Label className="text-xs">My Suppliers</Label>
                                  <p className="text-[11px] text-muted-foreground">Your company's own supplier list</p>
                                  <Select
                                    value={source?.supplier_id ?? '__none__'}
                                    onValueChange={(v) =>
                                      updateSource(slot, {
                                        supplier_id: v === '__none__' ? null : v,
                                      })
                                    }
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">— None —</SelectItem>
                                      {companySuppliers.map((cs) => (
                                        <SelectItem key={cs.id} value={cs.id}>{cs.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              {!source?.platform_supplier_id && !source?.supplier_id && (
                                <div className="space-y-1">
                                  <Label className="text-xs">Source Name</Label>
                                  <Input
                                    value={source?.source_name ?? ''}
                                    onChange={(e) => updateSource(slot, { source_name: e.target.value })}
                                    className="h-8 text-sm"
                                  />
                                </div>
                              )}

                              <div className="space-y-1">
                                <Label className="text-xs">Product URL</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={source?.url ?? ''}
                                    onChange={(e) => updateSource(slot, { url: e.target.value })}
                                    placeholder="https://..."
                                    className="h-8 text-sm flex-1"
                                  />
                                  {source?.url && /^https?:\/\//i.test(source.url) && (
                                    <a
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center h-8 w-8 rounded border bg-background text-amber-500 hover:text-amber-400 hover:bg-muted"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-end gap-2">
                                <div className="space-y-1 flex-1">
                                  <Label className="text-xs">Last Seen Price (R)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={source?.last_seen_price ?? ''}
                                    onChange={(e) => updateSource(slot, { last_seen_price: e.target.value })}
                                    className={`h-8 text-sm ${stale && source?.last_checked_at ? 'border-amber-500/50' : ''}`}
                                  />
                                </div>
                                {source?.id ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markChecked(source)}
                                    className="h-8 text-xs shrink-0"
                                  >
                                    Mark checked
                                  </Button>
                                ) : (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="shrink-0">
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            disabled
                                            className="h-8 text-xs shrink-0"
                                          >
                                            Mark checked
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Save sources first to enable mark checked
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Price sources can be added after the item is created.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <LineItemFormActions 

              isLoading={isLoading}
              isEditMode={isEditMode}
              onCancel={handleCancel}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default InternalLineItemForm;
